import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import CookieConsent from "./components/CookieConsent";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import SupportChatbot from "./components/SupportChatbot";
import ExitIntentPopup from "./components/ExitIntentPopup";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import SubscribePage from "./pages/SubscribePage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import DashboardPage from "./pages/DashboardPage";
import CVBuilderPage from "./pages/CVBuilderPage";
import JobMatcherPage from "./pages/JobMatcherPage";
import InterviewPrepPage from "./pages/InterviewPrepPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import TermsPage from "./pages/TermsPage";
import ProfileSettingsPage from "./pages/ProfileSettingsPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import TrainingPage from "./pages/TrainingPage";
import DocumentGeneratorPage from "./pages/DocumentGeneratorPage";
import JobBoardPage from "./pages/JobBoardPage";
import CommunityPage from "./pages/CommunityPage";
import SkillsAnalyzerPage from "./pages/SkillsAnalyzerPage";
import MockInterviewPage from "./pages/MockInterviewPage";
import SalaryCoachPage from "./pages/SalaryCoachPage";
import First90DaysPage from "./pages/First90DaysPage";
import EmailTemplatesPage from "./pages/EmailTemplatesPage";
import CareerPathPage from "./pages/CareerPathPage";
import WorkplaceRightsPage from "./pages/WorkplaceRightsPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import CVEnhancerPage from "./pages/CVEnhancerPage";
import CoverLetterPage from "./pages/CoverLetterPage";
import JobPipelinePage from "./pages/JobPipelinePage";
import JobMatchPage from "./pages/JobMatchPage";
import AboutPage from "./pages/AboutPage";
import GovJobsPage from "./pages/GovJobsPage";
import LearnershipFinderPage from "./pages/LearnershipFinderPage";
import SAJobBoardsPage from "./pages/SAJobBoardsPage";
import SupportingDocsPage from "./pages/SupportingDocsPage";
import CVReviewPage from "./pages/CVReviewPage";
import NotFoundPage from "./pages/NotFoundPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route Component (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      
      {/* Protected Routes */}
      <Route path="/subscribe" element={
        <ProtectedRoute>
          <SubscribePage />
        </ProtectedRoute>
      } />
      <Route path="/payment-success" element={
        <ProtectedRoute>
          <PaymentSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/cv-builder" element={
        <ProtectedRoute>
          <CVBuilderPage />
        </ProtectedRoute>
      } />
      <Route path="/cv-builder/:id" element={
        <ProtectedRoute>
          <CVBuilderPage />
        </ProtectedRoute>
      } />
      <Route path="/job-matcher" element={<Navigate to="/job-match" replace />} />
      <Route path="/interview-prep" element={
        <ProtectedRoute>
          <InterviewPrepPage />
        </ProtectedRoute>
      } />
      <Route path="/job-tracker" element={<Navigate to="/job-pipeline" replace />} />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfileSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <ProfileSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/training" element={
        <ProtectedRoute>
          <TrainingPage />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute>
          <DocumentGeneratorPage />
        </ProtectedRoute>
      } />
      <Route path="/supporting-documents" element={
        <ProtectedRoute>
          <SupportingDocsPage />
        </ProtectedRoute>
      } />
      <Route path="/cv-review" element={
        <ProtectedRoute>
          <CVReviewPage />
        </ProtectedRoute>
      } />
      <Route path="/job-board" element={
        <ProtectedRoute>
          <JobBoardPage />
        </ProtectedRoute>
      } />
      <Route path="/community" element={
        <ProtectedRoute>
          <CommunityPage />
        </ProtectedRoute>
      } />
      <Route path="/skills" element={
        <ProtectedRoute>
          <SkillsAnalyzerPage />
        </ProtectedRoute>
      } />
      <Route path="/mock-interview" element={
        <ProtectedRoute>
          <MockInterviewPage />
        </ProtectedRoute>
      } />
      <Route path="/follow-up" element={<Navigate to="/job-pipeline" replace />} />
      <Route path="/salary-coach" element={
        <ProtectedRoute>
          <SalaryCoachPage />
        </ProtectedRoute>
      } />
      <Route path="/first-90-days" element={
        <ProtectedRoute>
          <First90DaysPage />
        </ProtectedRoute>
      } />
      <Route path="/email-templates" element={
        <ProtectedRoute>
          <EmailTemplatesPage />
        </ProtectedRoute>
      } />
      <Route path="/career-path" element={
        <ProtectedRoute>
          <CareerPathPage />
        </ProtectedRoute>
      } />
      <Route path="/workplace-rights" element={
        <ProtectedRoute>
          <WorkplaceRightsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/cv-enhancer" element={
        <ProtectedRoute>
          <CVEnhancerPage />
        </ProtectedRoute>
      } />
      
      <Route path="/cover-letter" element={
        <ProtectedRoute>
          <CoverLetterPage />
        </ProtectedRoute>
      } />
      
      <Route path="/job-pipeline" element={
        <ProtectedRoute>
          <JobPipelinePage />
        </ProtectedRoute>
      } />
      
      <Route path="/job-match" element={
        <ProtectedRoute>
          <JobMatchPage />
        </ProtectedRoute>
      } />
      
      {/* SA-Specific Features */}
      <Route path="/gov-jobs" element={
        <ProtectedRoute>
          <GovJobsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/learnerships" element={
        <ProtectedRoute>
          <LearnershipFinderPage />
        </ProtectedRoute>
      } />
      
      <Route path="/sa-job-boards" element={
        <ProtectedRoute>
          <SAJobBoardsPage />
        </ProtectedRoute>
      } />
      
      {/* Redirect old SETA tracker to CV builder (merged feature) */}
      <Route path="/seta-tracker" element={<Navigate to="/cv-builder" replace />} />
      
      {/* Public Policy Pages */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/about" element={<AboutPage />} />
      
      {/* Auth Flow Pages */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Catch all - 404 page */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <div className="App">
            <AppRoutes />
            <Toaster position="top-center" richColors />
            <CookieConsent />
            <PWAInstallPrompt />
            <SupportChatbot />
            <ExitIntentPopup />
          </div>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
